import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  ImageList,
  ImageListItem,
  Menu,
  MenuItem,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  AddAPhoto,
  Map as MapIcon,
  FavoriteBorder,
  ArrowBack,
  MoreVert,
  Star,
  CalendarToday,
  Person,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { catService, commentService, getImageUrl, hasValidLocation } from '../../services/api';
import Comments from './Comments';

interface Cat {
  _id: string;
  name: string;
  breed: string;
  notes: string;
  imageUrl: string;
  additionalImages: Array<{
    _id: string;
    imageUrl: string;
    uploadedAt: string;
    uploadedBy?: { username: string };
  }>;
  location: { latitude: number; longitude: number };
  likes_pats: boolean;
  uploadedBy: { _id: string; username: string; profilePicture: string };
  createdAt: string;
}

const CatProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [cat, setCat] = useState<Cat | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addPhotoDialogOpen, setAddPhotoDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [photoMenuAnchor, setPhotoMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);

  const [editForm, setEditForm] = useState({
    name: '',
    breed: '',
    notes: '',
    likes_pats: false,
  });
  const [newPhoto, setNewPhoto] = useState<File | null>(null);
  const [newPhotoPreview, setNewPhotoPreview] = useState<string | null>(null);

  const canEdit = user && cat && (user.isAdmin || user._id === cat.uploadedBy._id);

  useEffect(() => {
    const fetchCat = async () => {
      if (!id) return;
      try {
        const data = await catService.getById(id);
        setCat(data);
        setEditForm({
          name: data.name,
          breed: data.breed || '',
          notes: data.notes || '',
          likes_pats: data.likes_pats,
        });
      } catch (error) {
        console.error('Failed to fetch cat:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCat();
  }, [id]);

  const handleEditSubmit = async () => {
    if (!id) return;
    try {
      const updated = await catService.update(id, editForm);
      setCat(updated);
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update cat:', error);
    }
  };

  const handleAddPhoto = async () => {
    if (!id || !newPhoto) return;
    try {
      const formData = new FormData();
      formData.append('photo', newPhoto);
      const updated = await catService.addPhoto(id, formData);
      setCat(updated);
      setAddPhotoDialogOpen(false);
      setNewPhoto(null);
      setNewPhotoPreview(null);
    } catch (error) {
      console.error('Failed to add photo:', error);
    }
  };

  const handleSetDefaultPhoto = async (photoId: string) => {
    if (!id) return;
    try {
      const updated = await catService.setDefaultPhoto(id, photoId);
      setCat(updated);
      setPhotoMenuAnchor(null);
    } catch (error) {
      console.error('Failed to set default photo:', error);
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!id) return;
    try {
      const updated = await catService.deletePhoto(id, photoId);
      setCat(updated);
      setPhotoMenuAnchor(null);
    } catch (error) {
      console.error('Failed to delete photo:', error);
    }
  };

  const handleDeleteCat = async () => {
    if (!id) return;
    try {
      await catService.delete(id);
      navigate('/cats');
    } catch (error) {
      console.error('Failed to delete cat:', error);
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewPhoto(file);
      const reader = new FileReader();
      reader.onload = () => setNewPhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!cat) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Cat not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/cats')} sx={{ mt: 2 }}>
          Back to Cats
        </Button>
      </Container>
    );
  }

  const allImages = [
    { _id: 'main', imageUrl: cat.imageUrl, isDefault: true },
    ...cat.additionalImages.map((img) => ({ ...img, isDefault: false })),
  ];

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        py: 4,
        background: 'linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 100%)',
      }}
    >
      <Container maxWidth="lg">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/cats')}
          sx={{ mb: 3, color: 'text.secondary' }}
        >
          Back to Cats
        </Button>

        <Paper
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: 'linear-gradient(145deg, #1a1a2e 0%, #16162a 100%)',
            border: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          {/* Hero Image */}
          <Box
            sx={{
              position: 'relative',
              height: { xs: 300, md: 450 },
              cursor: 'pointer',
            }}
            onClick={() => setSelectedImage(getImageUrl(cat.imageUrl))}
          >
            <img
              src={getImageUrl(cat.imageUrl)}
              alt={cat.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                p: 3,
              }}
            >
              <Typography variant="h3" fontWeight="bold">
                {cat.name}
              </Typography>
              {cat.breed && (
                <Typography variant="h6" color="text.secondary">
                  {cat.breed}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: { xs: 2, md: 4 } }}>
            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              {cat.likes_pats && (
                <Chip
                  icon={<FavoriteBorder />}
                  label="Likes Pats"
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              )}
              {hasValidLocation(cat) && (
                <Chip
                  icon={<MapIcon />}
                  label="View on Map"
                  onClick={() => navigate(`/?catId=${cat._id}`)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: 'rgba(6, 182, 212, 0.1)',
                    color: 'secondary.main',
                    '&:hover': { bgcolor: 'rgba(6, 182, 212, 0.2)' },
                  }}
                />
              )}
              {canEdit && (
                <>
                  <Chip
                    icon={<EditIcon />}
                    label="Edit"
                    onClick={() => setEditDialogOpen(true)}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    icon={<AddAPhoto />}
                    label="Add Photo"
                    onClick={() => setAddPhotoDialogOpen(true)}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    icon={<DeleteIcon />}
                    label="Delete"
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{
                      cursor: 'pointer',
                      color: 'error.main',
                      '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' },
                    }}
                  />
                </>
              )}
            </Box>

            {/* Info */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {new Date(cat.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.light' },
                }}
                onClick={() => navigate(`/users/${cat.uploadedBy._id}`)}
              >
                <Avatar
                  src={getImageUrl(cat.uploadedBy.profilePicture)}
                  sx={{ width: 24, height: 24 }}
                >
                  {cat.uploadedBy.username[0]}
                </Avatar>
                <Typography variant="body2">{cat.uploadedBy.username}</Typography>
              </Box>
            </Box>

            {/* Notes */}
            {cat.notes && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  About
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {cat.notes}
                </Typography>
              </Box>
            )}

            {/* Photo Gallery */}
            {allImages.length > 1 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Photos ({allImages.length})
                </Typography>
                <ImageList cols={4} gap={12} sx={{ mt: 2 }}>
                  {allImages.map((img) => (
                    <ImageListItem
                      key={img._id}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        position: 'relative',
                        '&:hover .photo-overlay': { opacity: 1 },
                      }}
                      onClick={() => setSelectedImage(getImageUrl(img.imageUrl))}
                    >
                      <img
                        src={getImageUrl(img.imageUrl)}
                        alt={cat.name}
                        style={{ height: 150, objectFit: 'cover' }}
                      />
                      {img.isDefault && (
                        <Chip
                          icon={<Star sx={{ fontSize: 14 }} />}
                          label="Default"
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            bgcolor: 'rgba(124, 58, 237, 0.9)',
                          }}
                        />
                      )}
                      {canEdit && !img.isDefault && (
                        <Box
                          className="photo-overlay"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPhotoId(img._id);
                              setPhotoMenuAnchor(e.currentTarget);
                            }}
                            sx={{ bgcolor: 'rgba(0,0,0,0.6)' }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </ImageListItem>
                  ))}
                </ImageList>
              </Box>
            )}

            <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

            {/* Comments */}
            <Comments catId={cat._id} />
          </Box>
        </Paper>
      </Container>

      {/* Lightbox */}
      <Dialog
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none' } }}
      >
        <img
          src={selectedImage || ''}
          alt={cat?.name}
          style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
          onClick={() => setSelectedImage(null)}
        />
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Cat</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            value={editForm.name}
            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
          />
          <TextField
            label="Breed"
            fullWidth
            value={editForm.breed}
            onChange={(e) => setEditForm({ ...editForm, breed: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={4}
            value={editForm.notes}
            onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={editForm.likes_pats}
                onChange={(e) => setEditForm({ ...editForm, likes_pats: e.target.checked })}
              />
            }
            label="Likes Pats"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSubmit}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Photo Dialog */}
      <Dialog
        open={addPhotoDialogOpen}
        onClose={() => {
          setAddPhotoDialogOpen(false);
          setNewPhoto(null);
          setNewPhotoPreview(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Photo</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {newPhotoPreview ? (
              <Box
                component="img"
                src={newPhotoPreview}
                sx={{
                  maxWidth: '100%',
                  maxHeight: 300,
                  borderRadius: 2,
                  mb: 2,
                }}
              />
            ) : (
              <Box
                sx={{
                  border: '2px dashed rgba(255,255,255,0.2)',
                  borderRadius: 2,
                  p: 4,
                  mb: 2,
                }}
              >
                <AddAPhoto sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">Select a photo</Typography>
              </Box>
            )}
            <Button variant="outlined" component="label">
              Choose Photo
              <input type="file" hidden accept="image/*" onChange={handlePhotoSelect} />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddPhotoDialogOpen(false);
              setNewPhoto(null);
              setNewPhotoPreview(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddPhoto} disabled={!newPhoto}>
            Upload Photo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Cat</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {cat.name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteCat}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Menu */}
      <Menu
        anchorEl={photoMenuAnchor}
        open={Boolean(photoMenuAnchor)}
        onClose={() => setPhotoMenuAnchor(null)}
      >
        <MenuItem onClick={() => selectedPhotoId && handleSetDefaultPhoto(selectedPhotoId)}>
          <Star sx={{ mr: 1, fontSize: 18 }} /> Set as Default
        </MenuItem>
        <MenuItem
          onClick={() => selectedPhotoId && handleDeletePhoto(selectedPhotoId)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Delete Photo
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CatProfile;
